import { API_URL } from '../configAndUrls.js'
import { apiRequest, getAuthHeaders, createMutationIdempotencyKey } from '../http.js'

export const deliverablesApi = {
getMyTasks: async (options = {}) => {
      const data = await apiRequest('/deliverables/my-tasks', options)
      return data.deliverables || []
    },
    getByAssignment: async (assignmentId, options = {}) => {
      const data = await apiRequest(`/deliverables?assignmentId=${assignmentId}`, options)
      return data.deliverables || []
    },
    getCountsByAssignments: async (assignmentIds, options = {}) => {
      const ids = (assignmentIds || []).filter(Boolean)
      if (!ids.length) return {}
      const data = await apiRequest(`/deliverables?assignmentIds=${ids.join(',')}`, options)
      return data.counts_by_assignment_id || {}
    },
    getById: async (id, options = {}) => {
      const data = await apiRequest(`/deliverables/${id}`, options)
      return data.deliverable
    },
    update: async (id, updates = {}) => {
      const data = await apiRequest(`/deliverables/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      })
      return data.deliverable
    },
    updateDefinition: async (id, updates = {}) => {
      const data = await apiRequest(`/deliverables/${id}/definition`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      })
      return data.deliverable
    },
    deleteDefinition: async (id) => {
      await apiRequest(`/deliverables/${id}/definition`, { method: 'DELETE' })
    },
    review: async (id, status) => {
      const data = await apiRequest(`/deliverables/${id}/review`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      })
      return data.deliverable
    },
    releaseToClient: async (id) => {
      const data = await apiRequest(`/deliverables/${id}/release-to-client`, {
        method: 'PATCH',
        idempotencyKey: createMutationIdempotencyKey('deliverable-release'),
      })
      return data.deliverable
    },
    getComments: async (id, options = {}) => {
      const data = await apiRequest(`/deliverables/${id}/comments`, options)
      return data.comments || []
    },
    addComment: async (id, content, isInternal = false) => {
      const data = await apiRequest(`/deliverables/${id}/comments`, {
        method: 'POST',
        body: JSON.stringify({ content, is_internal: isInternal }),
        idempotencyKey: createMutationIdempotencyKey('deliverable-comment'),
      })
      return data.comment
    },
    resolveComment: async (deliverableId, commentId) => {
      const data = await apiRequest(`/deliverables/${deliverableId}/comments/${commentId}/resolve`, {
        method: 'PATCH',
        idempotencyKey: createMutationIdempotencyKey('comment-resolve'),
      })
      return data.comment
    },
    reopenComment: async (deliverableId, commentId) => {
      const data = await apiRequest(`/deliverables/${deliverableId}/comments/${commentId}/reopen`, {
        method: 'PATCH',
        idempotencyKey: createMutationIdempotencyKey('comment-reopen'),
      })
      return data.comment
    },
    getBriefPresignedUpload: async (id, filename, contentType) => {
      const params = new URLSearchParams({ filename })
      if (contentType) params.set('contentType', contentType)
      const response = await fetch(`${API_URL}/deliverables/${id}/brief/presigned-upload?${params}`, {
        headers: getAuthHeaders(),
      })
      const data = await response.json().catch(() => ({}))
      if (response.status === 503 && data.error === 'S3_NOT_CONFIGURED') return null
      if (!response.ok) throw new Error(data.error || data.message || 'Failed to get brief upload URL')
      return data
    },
    patchBrief: async (id, body) => {
      const data = await apiRequest(`/deliverables/${id}/brief`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      })
      return data.deliverable
    },
    getBriefDownloadUrl: async (id) => {
      return apiRequest(`/deliverables/${id}/brief/download-url`)
    },
    confirmPosted: async (id) => {
      const data = await apiRequest(`/deliverables/${id}/confirm-posted`, { method: 'POST' })
      return data.deliverable
    },
    getPresignedUpload: async (id, filename, contentType) => {
      const params = new URLSearchParams({ filename })
      if (contentType) params.set('contentType', contentType)
      const response = await fetch(`${API_URL}/deliverables/${id}/presigned-upload?${params}`, {
        headers: getAuthHeaders(),
      })
      const data = await response.json().catch(() => ({}))
      if (response.status === 503 && data.error === 'S3_NOT_CONFIGURED') return null
      if (!response.ok) throw new Error(data.error || data.message || 'Failed to get upload URL')
      return data
    },
    confirmS3Upload: async (id, s3Key, publicUrl) => {
      const data = await apiRequest(`/deliverables/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ s3_key: s3Key, public_url: publicUrl }),
      })
      return data.deliverable
    },
    submit: async (id) => {
      const data = await apiRequest(`/deliverables/${id}/submit`, {
        method: 'POST',
        idempotencyKey: createMutationIdempotencyKey('deliverable-submit'),
      })
      return data.deliverable
    },
    upload: async (id, file) => {
      const formData = new FormData()
      formData.append('file', file)
      const response = await fetch(`${API_URL}/deliverables/${id}/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      })
      if (!response.ok) {
        const err = await response.json()
        throw new Error(err.error || 'Upload failed')
      }
      const data = await response.json()
      return data.deliverable
    },
}
